import type { SyntheticEvent } from 'react';

/**
 * Modal registry module that manages the state of modals in the application.
 * Uses a module pattern to encapsulate private state and expose a public API.
 */
export const modalRegistry = (() => {
  // Private variables
  const activeIds = new Set<string>();
  const listeners = new Set<() => void>();

  // Map to store the element that triggered each modal
  const triggerElements = new Map<string, HTMLElement>();

  /**
   * Notify all registered listeners about a state change.
   * @private
   */
  function notifyListeners(): void {
    listeners.forEach((listener) => listener());
  }

  /**
   * Get the triggering element from an event.
   * @private
   * @param event - The event object
   * @returns The element that triggered the event, or null
   */
  function getTriggerElement(event?: SyntheticEvent | Event): HTMLElement | null {
    if (!event) return null;

    // Handle React synthetic events
    if ('currentTarget' in event && event.currentTarget instanceof HTMLElement) {
      return event.currentTarget;
    }

    // Handle native DOM events
    if ('target' in event && event.target instanceof HTMLElement) {
      return event.target;
    }

    return null;
  }

  // Public API
  return {
    /**
     * Subscribe to changes in the modal registry.
     * @param listener - Function to call when modal state changes
     * @returns Function to unsubscribe the listener
     */
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Open a modal with the given ID.
     * If the modal is already open, this is a no-op.
     * @param id - Unique identifier for the modal
     */
    open(id?: string, event?: SyntheticEvent | Event): void {
      if (!id || typeof id !== 'string' || id.trim() === '') {
        return;
      }

      // Store the element that triggered the modal\
      const triggerElement = getTriggerElement(event);
      if (triggerElement) {
        triggerElements.set(id, triggerElement);
      }

      if (!activeIds.has(id)) {
        activeIds.add(id);
        notifyListeners();
      }
    },

    /**
     * Close a modal with the given ID.
     * If the modal is not open, this is a no-op.
     * @param id - Unique identifier for the modal
     */
    close(id?: string): void {
      if (!id || typeof id !== 'string' || id.trim() === '') {
        return;
      }

      if (activeIds.has(id)) {
        activeIds.delete(id);

        // Return focus to the trigger element
        const element = triggerElements.get(id);
        if (element && document.body.contains(element)) {
          setTimeout(() => element.focus(), 0);
        }
        triggerElements.delete(id);

        notifyListeners();
      }
    },

    /**
     * Check if a modal is currently active.
     * @param id - Unique identifier for the modal
     * @returns True if the modal is active, false otherwise
     */
    isActive(id: string): boolean {
      return activeIds.has(id);
    },

    /**
     * Get a list of all currently active modal IDs.
     * @returns Array of active modal IDs
     */
    getActiveIds(): Array<string> {
      return Array.from(activeIds);
    },
  };
})();
